import type { TicketStatistics } from '@easyhelp/shared';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { NO_DATA, formatDuration, formatShare } from './format';

interface Indicator {
  label: string;
  /** `null` = dato assente nel periodo: si mostra `NO_DATA`. */
  value: string | null;
  note: string;
}

/** Gli indicatori del periodo, ciascuno con la nota che dice su quanti ticket è calcolato. */
function indicatorsOf(stats: TicketStatistics): Indicator[] {
  const { received, discarded, workable, firstResponse, resolution, development, reopened } = stats;
  return [
    {
      label: 'Ticket ricevuti',
      value: String(received),
      note: `${workable} lavorabili · ${discarded} scartati`,
    },
    {
      label: 'Prima risposta',
      value: firstResponse.medianSeconds === null ? null : formatDuration(firstResponse.medianSeconds),
      note:
        firstResponse.count === 0
          ? 'Nessuna risposta inviata ai ticket del periodo'
          : `Mediana su ${firstResponse.count} ticket con risposta di ${workable} lavorabili`,
    },
    {
      label: 'Tempo di chiusura',
      value: resolution.medianSeconds === null ? null : formatDuration(resolution.medianSeconds),
      note:
        resolution.count === 0
          ? 'Nessun ticket del periodo è stato risolto'
          : `Mediana fino alla prima risoluzione, su ${resolution.count} risolti di ${workable} lavorabili`,
    },
    {
      label: 'Passati allo sviluppo',
      value: formatShare(development.count, workable),
      note: `${development.count} ticket su ${workable} lavorabili`,
    },
    {
      label: 'Riaperti',
      value: String(reopened.count),
      note: `Dopo la risoluzione, su ${resolution.count} risolti`,
    },
    {
      label: 'Scartati',
      value: String(discarded),
      note: 'Conteggiati a parte: fuori da lavorabili, tempi e distribuzioni',
    },
  ];
}

/**
 * Riquadri degli indicatori come lista di definizioni (`dl`): ogni etichetta ha il suo valore e la
 * nota, leggibili in ordine anche da un lettore di schermo.
 */
export function StatisticsIndicators({ stats }: { stats: TicketStatistics }) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
      }}
    >
      {indicatorsOf(stats).map((indicator) => (
        <Paper
          key={indicator.label}
          variant="outlined"
          sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}
        >
          <Typography component="dt" variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {indicator.label}
          </Typography>
          <Typography
            component="dd"
            variant={indicator.value === null ? 'body1' : 'h4'}
            color={indicator.value === null ? 'text.secondary' : 'text.primary'}
            sx={{ m: 0, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
          >
            {indicator.value ?? NO_DATA}
          </Typography>
          <Typography component="dd" variant="body2" color="text.secondary" sx={{ m: 0 }}>
            {indicator.note}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}
